import React, { useRef, useEffect } from "react";
import AllValues from "../AllValues/AllValues";
import { TextField, InputAdornment, Button, Icon } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { twMerge } from "tailwind-merge";

import './Text.scss';
import PropTypes from "prop-types";
import styled from "styled-components";

// Fixed the useTraceUpdate function
function useTraceUpdate(props) {
	const prev = React.useRef(props);
	React.useEffect(() => {
		const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
			if (prev.current[k] !== v) {
				ps[k] = [prev.current[k], v];
			}
			return ps;
		}, {});
		if (Object.keys(changedProps).length > 0) {
			console.log("Changed props:", changedProps);
		}
		prev.current = props;
	});
}

const Text = props => {
	const inputRef = useRef(null);
	const [cursorPosition, setCursorPosition] = React.useState(0);
	const [shouldSetCursor, setShouldSetCursor] = React.useState(false);

	// Function to add commas to number for display
	const addCommas = (num) => {
		if (num === '' || num === undefined || num === null) return '';
		
		// Convert to string and handle decimal places
		const numStr = num.toString();
		const parts = numStr.split('.');
		
		// Add commas to integer part
		const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
		
		// Return with decimal part if it exists
		return parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
	};

	// Function to remove commas from number
	const removeCommas = (str) => {
		if (typeof str !== 'string') return str;
		return str.replace(/,/g, '');
	};

	// Calculate cursor position after adding commas
	const getCursorPositionAfterFormatting = (originalValue, formattedValue, originalCursorPos) => {
		if (!props.numberCommas || props.subtype !== "number") {
			return originalCursorPos;
		}

		// If cursor is at the end, put it at the end of formatted value
		if (originalCursorPos >= originalValue.length) {
			return formattedValue.length;
		}

		// Count characters up to cursor position in original value
		let charactersBeforeCursor = 0;
		let formattedIndex = 0;

		for (let i = 0; i < originalValue.length && charactersBeforeCursor < originalCursorPos; i++) {
			// Skip commas in the formatted value
			while (formattedIndex < formattedValue.length && formattedValue[formattedIndex] === ',') {
				formattedIndex++;
			}
			
			if (formattedIndex < formattedValue.length) {
				charactersBeforeCursor++;
				formattedIndex++;
			}
		}

		return formattedIndex;
	};

	// Get the display value (with commas if numberCommas is true)
	const getDisplayValue = () => {
		let displayValue = props.formula ? String(props.formula(props.value)) : props.value;
		
		if (props.numberCommas && (props.subtype === "number" || typeof displayValue === 'number')) {
			return addCommas(displayValue);
		}
		
		return displayValue || '';
	};

	// Effect to set cursor position after formatting
	useEffect(() => {
		if (shouldSetCursor && inputRef.current && props.numberCommas && props.subtype === "number") {
			const input = inputRef.current;
			
			// Use requestAnimationFrame for more reliable cursor positioning
			requestAnimationFrame(() => {
				if (input === document.activeElement) {
					input.setSelectionRange(cursorPosition, cursorPosition);
				}
			});
			
			setShouldSetCursor(false);
		}
	}, [shouldSetCursor, cursorPosition, props.numberCommas, props.subtype]);

	// Determine the end adornment to use
	const getEndAdornment = () => {
		// If endAdornment is directly passed, use it with priority
		if (props.endAdornment) {
			return props.endAdornment;
		}
		
		// Otherwise, use the existing subtype logic
		if (props.subtype === "email") {
			return (
				<a className="end-adornment" href={`mailto:${props.value}`}>
					<Icon className="icon">send_icon</Icon>
				</a>
			);
		}
		
		if (props.subtype === "phone") {
			return (
				<a className="end-adornment" href={`tel:${props.value}`}>
					<Icon className="icon">phone_icon</Icon>
				</a>
			);
		}
		
		return null;
	};

	const handleInputChange = (event) => {
		const inputValue = event.target.value;
		const currentCursor = event.target.selectionStart;

		let rawValue = inputValue;
		if (props.numberCommas && props.subtype === "number") {
			rawValue = removeCommas(inputValue);
		}
		
		if (props.subtype === "number") {
			// Validate that it's a valid number format (allows empty, negative, and one decimal point)
			const numberRegex = /^-?\d*\.?\d*$/;
			if (rawValue !== '' && !numberRegex.test(rawValue)) {
				return; // Don't update if it's not a valid number format
			}
		}

		let processedValue = rawValue;
		if (props.subtype === "number" && rawValue !== '' && rawValue !== '-') {
			// If the raw value ends with a decimal, do not parse it to a float yet.
			// This allows the user to type a decimal point.
			if (rawValue.slice(-1) === '.') {
				processedValue = rawValue;
			} else {
				const parsed = parseFloat(rawValue);
				// Only update to a number if it's a valid number.
				if (!isNaN(parsed)) {
					processedValue = parsed;
				}
			}
		}

		if (props.numberCommas && props.subtype === "number") {
			const newFormattedValue = addCommas(rawValue);
			const newCursorPos = getCursorPositionAfterFormatting(
				removeCommas(inputValue),
				newFormattedValue,
				currentCursor - (inputValue.substring(0, currentCursor).match(/,/g) || []).length
			);
			
			setCursorPosition(newCursorPos);
			setShouldSetCursor(true);
		}
		
		props.onChange(processedValue, props.name);
	};

	const handleKeyDown = (event) => {
		// Handle special keys that might affect cursor position
		if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
			// Let the browser handle these naturally
			return;
		}
	};

	const handleClick = (event) => {
		// No need to manually track cursor position on click
		// Let the browser handle it naturally
	};

	const autoComplete = props.autoComplete ?? (props.disableAutofill ? "one-time-code" : undefined);
	const inputName = props.inputName ?? (props.name ? `seguro-${props.name}` : undefined);

	return (
		<React.Fragment>
			{props.element?.showHistoric && (
				<AllValues value={props.value} element={props.element} />
			)}
			
			<div className="relative w-full">
				<TextField
					variant="outlined"
					label={props.label}
					ref={inputRef}
					size="small"
					fullWidth
					disabled={props.readOnly}
					value={getDisplayValue()}
					type={props.subtype === "number" ? "text" : (props.subtype || "text")} // Use text for numbers to handle commas
					onChange={handleInputChange}
					onKeyDown={handleKeyDown}
					onClick={handleClick}
					placeholder={props.label}
					autoComplete={autoComplete}
					name={inputName}
					inputProps={{
						autoComplete,
						name: inputName,
					}}
					InputProps={{
						style:{
							borderRadius: "20px"
						}
				}}
				/>
				
				{/* Render start adornment if provided */}
				{props.labelAdornment && (
					<div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
						{props.labelAdornment}
					</div>
				)}
				
				{/* Render end adornment */}
				{getEndAdornment() && (
					<div className="absolute right-3 top-1/2 transform -translate-y-1/2">
						{getEndAdornment()}
					</div>
				)}
			</div>
		</React.Fragment>
	);
};

export default React.memo(Text);

Text.propTypes = {
	element: PropTypes.object,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	readOnly: PropTypes.bool,
	multiline: PropTypes.bool,
	moreRows: PropTypes.bool,
	showLabel: PropTypes.bool,
	labelInput: PropTypes.string,
	formula: PropTypes.func,
	subtype: PropTypes.string,
	labelAdornment: PropTypes.string,
	endAdornment: PropTypes.node,
	numberCommas: PropTypes.bool,
	onChange: PropTypes.func.isRequired,
	name: PropTypes.string,
	autoComplete: PropTypes.string,
	inputName: PropTypes.string,
	disableAutofill: PropTypes.bool,
};